import { useRef, useState } from 'react';
import type { AparteMessage, AparteSegment } from '../types.js';
import type { AparteChatImperativeApi } from '@aparte/core';

/**
 * Idiomatic React ergonomics for `<AparteChat>`. Owns the `messages` state and
 * the component ref so the consumer doesn't have to wire `onMessagesChange`
 * back into `messages` by hand. Spread `ref` + `messages` + `onMessagesChange`
 * onto `<AparteChat>` and drive it with the returned imperative helpers.
 *
 * @example
 * const chat = useAparteChat();
 * return (
 *   <AparteChat
 *     ref={chat.ref}
 *     messages={chat.messages}
 *     onMessagesChange={chat.setMessages}
 *   />
 * );
 * // The user's message is appended automatically on send — don't re-add it.
 */
export interface UseAparteChat {
    messages: AparteMessage[];
    setMessages: React.Dispatch<React.SetStateAction<AparteMessage[]>>;
    // Typed to match `<AparteChat>`'s forwardRef target so `ref={chat.ref}`
    // typechecks verbatim — @types/react 18.3's variance rejects the
    // useRef(null) `RefObject<T | null>` shape against `Ref<T>`, so the (safe)
    // cast lives here once, not in every consumer.
    ref: React.RefObject<AparteChatImperativeApi>;
    // ── imperative helpers (delegate to the component handle) ──
    appendMessage: (message: AparteMessage, options?: { historical?: boolean }) => void;
    updateMessage: (messageId: string, updates: Partial<AparteMessage>) => void;
    updateLastMessage: (content: string, options?: { append?: boolean }) => void;
    addSegment: (segment: AparteSegment) => void;
    updateSegment: (segmentId: string, updates: Partial<AparteSegment>) => void;
    removeSegment: (segmentId: string) => void;
    appendToSegment: (segmentId: string, content: string) => void;
    /**
     * The host's list — the authority, and mid-stream a frame ahead of the
     * `messages` state, which is synced once per paint rather than per token.
     */
    getMessages: () => AparteMessage[];
    clearMessages: (options?: { revokeAttachments?: boolean }) => void;
    addBranch: (messageId: string) => number;
    addSiblingOf: (existingId: string, message: AparteMessage) => string | null;
    truncateFrom: (messageId: string) => void;
    truncateResponsesAfter: (userMessageId: string) => void;
    injectTokenStream: (messageId: string, tokens: AsyncIterable<string>) => Promise<void>;
    stopTokenStream: () => void;
    setConversationId: (id: string | null) => Promise<void>;
    isStreaming: () => boolean;
    scrollToBottom: () => void;
    focusInput: () => void;
    /** The `<aparte-chat-viewport>` element — same accessor on all four wrappers. */
    getViewport: () => HTMLElement | null;
}

export function useAparteChat(initial: AparteMessage[] = []): UseAparteChat {
    const [messages, setMessages] = useState<AparteMessage[]>(initial);
    const ref = useRef<AparteChatImperativeApi | null>(null);
    const h = () => ref.current;
    return {
        messages,
        setMessages,
        ref: ref as React.RefObject<AparteChatImperativeApi>,
        appendMessage: (m, o) => h()?.appendMessage(m, o),
        updateMessage: (id, u) => h()?.updateMessage(id, u),
        updateLastMessage: (c, o) => h()?.updateLastMessage(c, o),
        addSegment: (s) => h()?.addSegment(s),
        updateSegment: (id, u) => h()?.updateSegment(id, u),
        removeSegment: (id) => h()?.removeSegment(id),
        appendToSegment: (id, c) => h()?.appendToSegment(id, c),
        getMessages: () => h()?.getMessages() ?? messages,
        clearMessages: (o) => h()?.clearMessages(o),
        addBranch: (id) => h()?.addBranch(id) ?? 0,
        addSiblingOf: (id, m) => h()?.addSiblingOf(id, m) ?? null,
        truncateFrom: (id) => h()?.truncateFrom(id),
        truncateResponsesAfter: (id) => h()?.truncateResponsesAfter(id),
        injectTokenStream: (id, tokens) => h()?.injectTokenStream(id, tokens) ?? Promise.resolve(),
        stopTokenStream: () => h()?.stopTokenStream(),
        setConversationId: (id) => h()?.setConversationId(id) ?? Promise.resolve(),
        isStreaming: () => h()?.isStreaming() ?? false,
        scrollToBottom: () => h()?.scrollToBottom(),
        focusInput: () => h()?.focusInput(),
        getViewport: () => h()?.getViewport() ?? null,
    };
}
